import { resolveByosImgixSrc, withByosImgixServerSign } from '@air/component-imgix-image';

export type CanvasOutputType = 'image/jpeg' | 'image/png' | 'image/webp';

const ALWAYS_SUPPORTED_TYPES: ReadonlySet<string> = new Set(['image/jpeg', 'image/png']);
export const JPEG_QUALITY = 0.92;
export const ROTATION_90 = 90;
export const ROTATION_270 = 270;

/** Detect webp toBlob support once (false on Safari) */
let webpSupported: boolean | undefined;
const supportsWebpToBlob = (): boolean => {
  if (webpSupported === undefined) {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    webpSupported = c.toDataURL('image/webp').startsWith('data:image/webp');
  }

  return webpSupported;
};

export const isSupportedType = (contentType: string): contentType is CanvasOutputType =>
  ALWAYS_SUPPORTED_TYPES.has(contentType) || (contentType === 'image/webp' && supportsWebpToBlob());

/** File extensions that browsers cannot decode via `new Image()` but Imgix can transcode. */
const BROWSER_INCOMPATIBLE_EXTENSIONS: ReadonlySet<string> = new Set(['heic', 'heif', 'tiff', 'tif']);

/**
 * For Imgix URLs pointing to browser-incompatible formats (HEIC, HEIF, TIFF),
 * appends `fm=png` so Imgix transcodes to a format the browser can decode.
 * Non-Imgix URLs and already-compatible formats are returned unchanged.
 */
const toBrowserCompatibleUrl = (url: string): string => {
  try {
    const urlObj = new URL(resolveByosImgixSrc(url));
    const extension = urlObj.pathname.split('.').pop()?.toLowerCase();

    if (!extension || !BROWSER_INCOMPATIBLE_EXTENSIONS.has(extension)) {
      return url;
    }

    if (!urlObj.hostname.endsWith('.imgix.net')) {
      return url;
    }

    if (!urlObj.searchParams.has('fm')) {
      urlObj.searchParams.set('fm', 'png');
    }

    return urlObj.toString();
  } catch {
    return url;
  }
};

export const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = withByosImgixServerSign(toBrowserCompatibleUrl(url));
  });

export const canvasToBlob = (canvas: HTMLCanvasElement, contentType: CanvasOutputType): Promise<Blob> => {
  const quality = contentType === 'image/jpeg' ? JPEG_QUALITY : undefined;

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      },
      contentType,
      quality,
    );
  });
};

const REVOKE_DELAY_MS = 100;

export const downloadBlob = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, REVOKE_DELAY_MS);
};
